import Button from 'components/Button'
import capitalize from 'lodash/fp/capitalize'
import type { BuiltInProviderType } from 'next-auth/providers'
import { getProviders, signIn } from 'next-auth/react'
import patternMatching from 'utils/patternMatching'
import GoogleIcon from 'assets/icons/google.svg'
import FacebookIcon from 'assets/icons/facebook.svg'
import DiscordIcon from 'assets/icons/discord.svg'
import { Trans, useTranslation } from 'next-i18next'
import { ReactElement, useEffect, useState } from 'react'
import Image from 'next/image'
import logger from 'utils/logger'
import ErrorMessage from 'components/ErrorMessage'
import Loading from 'components/Loading'
import FormField from 'forms/FormField'
import { useRouter } from 'next/router'
import { useGlobalStore } from 'stores/global.store'

type Action = 'login' | 'signup' | 'link' | 'vote'

type Props = {
  type: BuiltInProviderType
  action: Action
  disabled?: boolean
  callbackUrl?: string
  onClick?: () => void
}

const AuthProviderButton = ({
  type,
  action,
  disabled,
  callbackUrl,
  onClick,
}: Props) => {
  const router = useRouter()
  const { platform } = useGlobalStore()
  const { t } = useTranslation()
  const [clicked, setClicked] = useState(false)

  const [email, setEmail] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  if (clicked && type === 'email') {
    return (
      <form
        className="py-5 space-y-2"
        onSubmit={(e) => {
          e.preventDefault()

          setSendingEmail(true)

          signIn(type, {
            callbackUrl,
            locale: router.locale,
            email: [email, router.locale || 'en', platform || 'influexer'].join(
              '::',
            ),
          })
        }}
      >
        <FormField
          placeholder={t('socialProviderButton:labelEmail')}
          value={email}
          onChange={(e: any) => {
            setEmail(e.target.value)
          }}
          $size="lg"
          type="email"
          name="email"
        />

        <Button $fluid disabled={!email || sendingEmail}>
          {t('socialProviderButton:emailButtonSend')}
        </Button>
        <Button
          $fluid
          $type="tertiary"
          type="button"
          onClick={() => {
            setClicked(false)
          }}
          disabled={sendingEmail}
        >
          {t('socialProviderButton:emailButtonCancel')}
        </Button>
      </form>
    )
  }

  return (
    <div className="py-2">
      <Button
        type="button"
        $type="secondary"
        $fluid
        onClick={() => {
          if (!clicked) {
            setClicked(true)
            onClick?.()

            if (type !== 'email') {
              signIn(type, {
                callbackUrl,
              })
            }
          }
        }}
        disabled={disabled}
        className="flex items-center"
      >
        {type === 'google' && <GoogleIcon className="w-6 h-6" />}
        {type === 'facebook' && <FacebookIcon className="w-6 h-6" />}
        {type === 'discord' && <DiscordIcon className="w-6.5 " />}
        {type === 'email' && (
          <Image
            src="/assets/icons/email.png"
            width="24"
            height="24"
            alt="Email Icon"
          />
        )}

        <div className="mx-auto">
          {clicked
            ? t('socialProviderButton:loading')
            : patternMatching<Action, ReactElement>([
                [
                  'login',
                  <Trans
                    key="socialProviderButton:login"
                    i18nKey="socialProviderButton:login"
                    values={{
                      socialMediaProvider: capitalize(type),
                    }}
                  />,
                ],
                [
                  'signup',
                  <Trans
                    key="socialProviderButton:signup"
                    i18nKey="socialProviderButton:signup"
                    values={{
                      socialMediaProvider: capitalize(type),
                    }}
                  />,
                ],
                [
                  'vote',
                  <Trans
                    key="socialProviderButton:voteWith"
                    i18nKey="socialProviderButton:voteWith"
                    values={{
                      socialMediaProvider: capitalize(type),
                    }}
                  />,
                ],
                [
                  'link',
                  disabled ? (
                    <Trans
                      key="socialProviderButton:linkDisabled"
                      i18nKey="socialProviderButton:linkDisabled"
                      values={{
                        socialMediaProvider: capitalize(type),
                      }}
                    />
                  ) : (
                    <Trans
                      key="socialProviderButton:linkEnabled"
                      i18nKey="socialProviderButton:linkEnabled"
                      values={{
                        socialMediaProvider: capitalize(type),
                      }}
                    />
                  ),
                ],
              ])(action)}
        </div>
      </Button>
    </div>
  )
}

export default AuthProviderButton

export const ListAuthProviderButtons = ({
  action,
  callbackUrl,
  currentProviders,
  ignoreProviders,
}: {
  action: Action
  callbackUrl?: string
  currentProviders?: string[]
  ignoreProviders?: string[]
}) => {
  const [clicked, setClicked] = useState<BuiltInProviderType | null>(null)
  const [providers, setProviders] = useState<BuiltInProviderType[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getProviders()
      .then((providers) => {
        if (providers) {
          setProviders(
            (Object.keys(providers) as BuiltInProviderType[])
              .filter(
                (provider) =>
                  provider !== 'credentials' &&
                  (provider as string) /* 'token' is a custom credentials provider */ !==
                    'token',
              )
              .filter((provider) => {
                return !ignoreProviders || !ignoreProviders.includes(provider)
              }),
          )
        }
      })
      .catch((error) => {
        logger.fatal(`Could not load Login Providers`, {
          error,
          origin: 'front-end',
        })
        setError(
          error.message ||
            "We couldn't load the Providers. Please try again or contact support.",
        )
      })
  }, [])

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>
  }

  if (!providers) {
    return <Loading $size="lg" $type="logo" />
  }

  return (
    <ul>
      {providers.map((provider) => (
        <li key={`auth-provider-${provider}`}>
          <AuthProviderButton
            type={provider}
            action={action}
            callbackUrl={callbackUrl}
            disabled={
              currentProviders?.includes(provider) ||
              (!!clicked && clicked !== provider)
            }
            onClick={() => {
              if (provider !== 'email') {
                setClicked(provider)
              }
            }}
          />
        </li>
      ))}
    </ul>
  )
}
